import { ReactNode } from 'react';

import { GridLink } from '@components/gridLink/GridLink';
import { IconFavorite } from '@components/iconButton/IconFavorite';
import { TableGridBaseColDef } from '@components/table/types/TableColumnDef.type';
import { OverflowTooltip, Tooltip } from '@components/tooltip';
import {
  ColumnType,
  ExternalQuoteStatus,
  InternalQuoteStatus,
  QuoteStatus,
  QuoteStatusId,
  QuoteType,
} from '@models/quotes';
import { ClockFormat } from '@lio/rule-engine';

import { GridValidRowModel, GridCellParams, GridComparatorFn, gridDateComparator } from '@mui/x-data-grid';
import { QuoteColumnField } from '@pages/Quotes/QuotesTable/QuoteColumnField.enum';
import { formatAddress } from '@utils/address';
import { formatDateToTimezone, getDateFormatter, LongDateFormatWithoutSeconds, ShortDateFormat } from '@utils/date';
import { formatCurrency } from '@utils/number';
import { format } from 'date-fns-tz';
import { UseMutateAsyncFunction, UseMutateFunction } from '@tanstack/react-query';
import { UpdateQuoteRequest } from '@services/quotes/useUpdateQuote';

import {
  QuoteSearchResponse,
  UserPinnedQuoteDeleteRequestParams,
  UserPinnedQuotePostRequestBody,
  UserPinnedQuotesGetRequestParams,
  UserWatchedQuotePostRequestBody,
} from '@lio/api-types';
import { Box, Typography } from '@mui/material';
import { UnderwritingIssue } from '@models/underwritingIssue';
import { EllipsisH } from '@assets/svgs';
import { MenuButton, MenuItemType } from '@components/menu';
import {
  cancelDeclineQuoteStatuses,
  QuoteDataGridType,
  quoteStatuses,
  UnderwriterHomePageTabs,
} from '@pages/Quotes/QuotesTable/DataGrid.constants';
import { IUserRole } from '@store/types/UserStoreSlice.type';
import { getFormattedPolicyNumber } from '@utils/policy';
import { TransactionType } from '@models/policyTransaction';
import { ImportantFlagColumn } from '@pages/Quotes/QuotesTable/columns/ImportantFlagColumn';
import { UnreadMessageNotificationColumn } from '@pages/Quotes/QuotesTable/columns/UnreadMessageNotificationColumn';
import { getQuoteOriginLabel } from '@components/table/filters/QuoteOriginTypeaheadFilter';
import { ReportingQuoteSourceLabels } from '@models/quotes';
import { getDisplayedWritingCompanyLabel, getWritingCompanyTooltip } from '@utils/issuingWritingCompany';

export type QuoteRow = QuoteSearchResponse['items'][0];

export type QuoteDataGridDef<T extends GridValidRowModel> = {
  isVisible?: (viewType: QuoteDataGridType) => boolean;
} & TableGridBaseColDef<T>;

const getApplyStringFilter =
  (value: string) =>
  (params: GridCellParams): boolean =>
    params.value?.toString().includes(value) || false;

const idComparator: GridComparatorFn<string> = (v1, v2, cellParams1, cellParams2) =>
  cellParams1.id.toString().localeCompare(cellParams2.id.toString());
const dateComparator: GridComparatorFn<string> = (v1, v2, cellParams1, cellParams2) =>
  gridDateComparator(new Date(v1).getTime(), new Date(v2).getTime(), cellParams1, cellParams2);
const disableComparator: GridComparatorFn<string> = () => 0;

export function getColumns({
  viewType,
  userConfig,
  modalConfig,
}: {
  viewType: QuoteDataGridType;
  userConfig: {
    currentUserId: string;
    userAuth: IUserRole;
    layoutPreferences: {
      clockFormat: ClockFormat;
    };
  };
  modalConfig: {
    showCopyQuoteModal: (quoteId: string) => void;
    showCancelQuoteModal: (quoteId: string, policyNumber: string) => void;
    showDeclineQuoteModal: (
      quoteId: string,
      policyNumber: string,
      quoteStatus: string,
      customerId: string,
      brokerageId: string,
      hasInternalOnlyAccessRestriction: boolean,
    ) => void;
    showFlagForReferralModal: (quoteId: string) => void;
    showRoleManagementModal: (quoteId: string) => void;
    showDuplicateQuoteModal: (quoteId: string) => void;
    updateQuote: UseMutateAsyncFunction<any, Error, UpdateQuoteRequest, unknown>;

    // functions
    deletePinnedQuote: UseMutateFunction<any, Error, UserPinnedQuoteDeleteRequestParams, unknown>;
    insertPinnedQuote: UseMutateFunction<
      UserPinnedQuotePostRequestBody,
      Error,
      UserPinnedQuotePostRequestBody & UserPinnedQuotesGetRequestParams,
      unknown
    >;
    deleteWatchedQuote: UseMutateFunction<any, Error, UserWatchedQuotePostRequestBody, unknown>;
    createWatchedQuote: UseMutateFunction<any, Error, UserWatchedQuotePostRequestBody, unknown>;
  };
}): QuoteDataGridDef<QuoteRow>[] {
  const { userAuth, currentUserId } = userConfig;
  const { isInternalUser, isInternalWithoutSales, isUnderwriter, isLIOContractor } = userAuth;

  const isUWHomePageView =
    viewType === QuoteDataGridType.UWHomePageAllTransaction || viewType === QuoteDataGridType.UWHomePageRecentlyViewed;

  const handlePinnedQuoteChange = ({ quoteId, isCurrentlyPinned }: { quoteId: string; isCurrentlyPinned: boolean }) => {
    if (isCurrentlyPinned) {
      modalConfig.deletePinnedQuote({
        quoteId,
        userId: currentUserId as string,
      });
    } else {
      modalConfig.insertPinnedQuote({
        quoteId,
        userId: currentUserId as string,
      });
    }
  };

  const baseColumns: QuoteDataGridDef<QuoteRow>[] = [
    // icon fields (always first)
    {
      field: QuoteColumnField.FlaggedAsImportant,
      type: ColumnType.Action,
      flex: 0,
      // these together will make the column 20px wide, overriding the default mui 50
      minWidth: 30,
      maxWidth: 30,
      isVisible: () => false,
      renderCell: (params) => (
        <ImportantFlagColumn isImportant={params.row.uwFlaggedAsImportant} quoteId={params.row.id} />
      ),
      mobileConfig: {
        hidden: {
          xs: true,
        },
      },
    },
    {
      field: QuoteColumnField.Favorite,
      type: ColumnType.Action,
      // these together will make the column 20px wide, overriding the default mui 50
      minWidth: isUWHomePageView ? 30 : 40,
      maxWidth: isUWHomePageView ? 30 : 40,
      isVisible: (viewType) =>
        [QuoteDataGridType.QuoteTypeNewBusiness, QuoteDataGridType.QuoteTypeRenewal].includes(viewType),
      renderCell: (params) => (
        <IconFavorite
          key='favorite-button'
          isFavorite={params.row.isPinned}
          onClick={() => handlePinnedQuoteChange({ quoteId: params.row.id, isCurrentlyPinned: params.row.isPinned })}
        />
      ),
      mobileConfig: {
        hidden: {
          xs: true,
        },
      },
    },
    {
      field: QuoteColumnField.UnreadMessageNotification,
      type: ColumnType.Action,
      // these together will make the column 20px wide, overriding the default mui 50
      minWidth: isUWHomePageView ? 30 : 40,
      maxWidth: isUWHomePageView ? 30 : 40,
      isVisible: (viewType) => UnderwriterHomePageTabs.includes(viewType),
      renderCell: (params) => (
        <UnreadMessageNotificationColumn
          unreadMessageCount={params.row.notifications?.length ?? 98}
          quoteId={params.row.id}
        />
      ),
      mobileConfig: {
        hidden: {
          xs: true,
        },
      },
    },

    {
      field: QuoteColumnField.QuoteId,
      headerName: 'Quote ID',
      type: ColumnType.String,
      flex: 0.5,
      minWidth: 120,
      sortComparator: idComparator,
      getApplyQuickFilterFn: getApplyStringFilter,
      renderCell: (params) => (
        <OverflowTooltip tooltipText={params.row.id}>
          <GridLink to={`/quote/${params.row.id}`}>{params.row.id}</GridLink>
        </OverflowTooltip>
      ),
      isVisible: () => true,
    },
    // quote/policy number fields
    {
      field: QuoteColumnField.QuoteNumber,
      headerName: 'Quote Number',
      type: ColumnType.String,
      minWidth: 120,
      sortComparator: idComparator,
      renderCell: (params) => (
        <OverflowTooltip tooltipText={params.row.policyNumber?.policyNumber || '-'}>
          <GridLink to={`/quote/${params.row.id}`}>{params.row.policyNumber?.policyNumber || '-'}</GridLink>
        </OverflowTooltip>
      ),
      isVisible: (viewType) => [QuoteDataGridType.QuoteTypeNewBusiness].includes(viewType),
      mobileConfig: {
        mobileLockedColumn: true,
      },
    },
    {
      field: QuoteColumnField.QuoteNumber,
      headerName: 'Policy Number',
      type: ColumnType.String,
      minWidth: 120,
      sortComparator: idComparator,
      renderCell: (params) => (
        <OverflowTooltip tooltipText={params.row.policyNumber?.policyNumber || '-'}>
          <GridLink to={`/quote/${params.row.id}`}>{params.row.policyNumber?.policyNumber || '-'}</GridLink>
        </OverflowTooltip>
      ),
      isVisible: (viewType) => [QuoteDataGridType.QuoteTypeRenewal].includes(viewType),
      mobileConfig: {
        mobileLockedColumn: true,
      },
    },
    {
      field: QuoteColumnField.QuoteNumber,
      headerName: 'Quote/Policy #',
      type: ColumnType.String,
      minWidth: 140,
      sortComparator: idComparator,
      renderCell: (params) => {
        let displayValue = params.row.policyNumber?.policyNumber || '-';

        if (params.row.quoteType !== QuoteType.NewBusiness) {
          const policyPrefix = params.row.policyNumber?.policies?.[0]?.policyNumberPrefix ?? '';
          displayValue = getFormattedPolicyNumber(policyPrefix, displayValue) || '-';
        }
        return (
          <OverflowTooltip tooltipText={displayValue}>
            <GridLink to={`/quote/${params.row.id}`}>{displayValue}</GridLink>
          </OverflowTooltip>
        );
      },
      isVisible: (viewType) => UnderwriterHomePageTabs.includes(viewType),
      mobileConfig: {
        mobileLockedColumn: true,
      },
    },
    // end  quote/policy number fields
    {
      field: QuoteColumnField.AccountName,
      headerName: 'Customer Name',
      type: ColumnType.String,
      flex: 0.5,
      minWidth: 150,
      valueGetter: (value, row) => row.customer?.name || '-',
      isVisible: () => true,
      renderCell: (params) => {
        return isInternalUser ? (
          <OverflowTooltip tooltipText={params.row.customer.name || '-'}>
            <GridLink to={`/customer/${params.row.customer.id}`}>{params.row.customer.name || '-'}</GridLink>
          </OverflowTooltip>
        ) : (
          <OverflowTooltip>{params.row.customer.name || '-'}</OverflowTooltip>
        );
      },
    },
    {
      field: QuoteColumnField.Address,
      headerName: 'Address',
      type: ColumnType.String,
      flex: 0.6,
      sortable: false,
      minWidth: 160,
      valueGetter: (_value, row) => {
        const { primaryQuoteLocation } = row;
        if (primaryQuoteLocation?.insuredLocation) {
          return formatAddress(primaryQuoteLocation.insuredLocation.address);
        }
        return '-';
      },
      isVisible: () => true,
    },
    {
      field: QuoteColumnField.QuoteType,
      headerName: isUWHomePageView ? 'Transaction Type' : 'Quote Type',
      type: ColumnType.String,
      flex: 0.5,
      minWidth: 130,
      valueGetter: (_value, row) => {
        if (row.quoteType === QuoteType.NewBusiness && row.bindWithRenewalTerm) {
          return TransactionType[QuoteType.Renewal];
        }
        return TransactionType[row.quoteType] || '-';
      },
      isVisible: () =>
        [
          QuoteDataGridType.QuoteTypeNewBusiness,
          QuoteDataGridType.QuoteTypeRenewal,
          QuoteDataGridType.UWHomePageAllTransaction,
          QuoteDataGridType.UWHomePageRecentlyViewed,
        ].includes(viewType),
    },
    {
      field: QuoteColumnField.Underwriter,
      headerName: 'Underwriter',
      type: ColumnType.String,
      flex: 0.5,
      minWidth: 130,
      sortComparator: disableComparator,
      valueGetter: (value, row) =>
        row.underwriter?.user?.firstName && row.underwriter?.user?.lastName
          ? `${row.underwriter?.user?.firstName} ${row.underwriter?.user?.lastName}`
          : '-',
      isVisible: () => true,
    },
    {
      field: QuoteColumnField.WritingCompany,
      headerName: 'Writing Company',
      type: ColumnType.String,
      flex: 0.5,
      minWidth: 130,
      // LIO-29798: sorting intentionally continues to key off the underlying
      // writingCompanyId — an overridden quote sorting among LIC rows while
      // displaying as LSIC is intended, not a bug.
      sortComparator: disableComparator,
      valueGetter: (value, row) =>
        getDisplayedWritingCompanyLabel({
          writingCompanyName: row.writingCompany?.name,
          issuingWritingCompanyId: row.issuingWritingCompany?.id,
        }),
      renderCell: (params) => {
        const tooltip = getWritingCompanyTooltip(params.row.issuingWritingCompany?.id);
        return tooltip ? (
          <Tooltip title={tooltip}>
            <Typography variant='bodySmall'>{params.value}</Typography>
          </Tooltip>
        ) : (
          <Typography variant='bodySmall'>{params.value}</Typography>
        );
      },
      isVisible: () => true,
    },
    {
      field: QuoteColumnField.EffectiveDate,
      headerName: 'Effective Date',
      type: ColumnType.String,
      flex: 0.5,
      minWidth: 130,
      valueGetter: (value, row) => (row.effectiveOn ? formatDateToTimezone(row.effectiveOn) : '-'),
      isVisible: () => true,
    },
    {
      field: QuoteColumnField.ProductOffering,
      headerName: 'Product',
      type: ColumnType.String,
      flex: 0.5,
      minWidth: 130,
      valueGetter: (value, row) => row.offering?.label || row.offering?.insuranceProduct?.name || '-',
      isVisible: () => true,
    },
    {
      field: QuoteColumnField.ProductLabel,
      headerName: 'Product Label',
      type: ColumnType.String,
      flex: 0.5,
      minWidth: 130,
      sortComparator: disableComparator,
      valueGetter: (value, row) => row.productLabel?.name || '-',
      isVisible: () => true,
    },
    {
      field: QuoteColumnField.BusinessClass,
      headerName: 'Business Class',
      type: ColumnType.String,
      flex: 0.5,
      minWidth: 130,
      valueGetter: (value, row) => row.productOption?.name || '-',
      isVisible: () => true,
    },
    {
      field: QuoteColumnField.Brokerage,
      headerName: 'Brokerage',
      type: ColumnType.String,
      flex: 0.5,
      minWidth: 130,
      valueGetter: (value, row) => row.brokerage?.name || '-',
      isVisible: () => true,
    },
    {
      field: QuoteColumnField.Producer,
      headerName: 'Producer Name',
      type: ColumnType.String,
      flex: 0.5,
      minWidth: 130,
      sortComparator: disableComparator,
      valueGetter: (value, row) =>
        row.brokerageProducer?.user?.firstName && row.brokerageProducer?.user?.lastName
          ? `${row.brokerageProducer?.user?.firstName} ${row.brokerageProducer?.user?.lastName}`
          : '-',
      isVisible: () => true,
    },
    {
      field: QuoteColumnField.TotalPremium,
      headerName: 'Total Premium',
      type: ColumnType.String,
      flex: 0.5,
      minWidth: 120,
      align: 'right',
      getApplyQuickFilterFn: getApplyStringFilter,
      sortComparator: disableComparator,
      valueGetter: (_, row) => (row.totalPremium ? `${formatCurrency(row.totalPremium)}` : '-'),
      isVisible: () => true,
    },
    {
      field: QuoteColumnField.Status,
      headerName: 'Status',
      type: ColumnType.String,
      flex: 0.5,
      minWidth: 160,
      valueGetter: (value, row) => {
        const { statusId } = row;
        if (statusId in InternalQuoteStatus && statusId in ExternalQuoteStatus) {
          return isInternalUser ? InternalQuoteStatus[statusId] : ExternalQuoteStatus[statusId];
        }
        return '-';
      },
      isVisible: () => true,
    },
    {
      field: QuoteColumnField.UnderwritingStatus,
      headerName: 'Underwriting Status',
      type: ColumnType.String,
      sortable: false,
      flex: 0.5,
      minWidth: 160,
      isVisible: () => true,
      renderCell: (params) => {
        if (isInternalUser) {
          type UWActivity = { underwritingActivityType?: { label?: string }; createdAt?: string };
          const activities: UWActivity[] = (params.row as any).underwritingActivities ?? [];
          const primaryLabel = activities[0]?.underwritingActivityType?.label ?? '-';
          const additionalCount = activities.length > 1 ? activities.length - 1 : 0;
          const displayValue = additionalCount > 0 ? `${primaryLabel} +${additionalCount}` : primaryLabel;
          const tooltipContent =
            activities.length > 0 ? (
              <>
                {activities.map((activity, idx) => {
                  const label = activity.underwritingActivityType?.label ?? '-';
                  const dateStr = activity.createdAt
                    ? format(
                        new Date(activity.createdAt),
                        getDateFormatter(userConfig.layoutPreferences.clockFormat, LongDateFormatWithoutSeconds),
                      )
                    : '-';
                  return <div key={idx}>{`${label}: ${dateStr}`}</div>;
                })}
              </>
            ) : (
              ''
            );

          return (
            <Tooltip title={tooltipContent}>
              <Typography variant='bodySmall'>{displayValue}</Typography>
            </Tooltip>
          );
        } else if (!isInternalUser && params.row.statusId === QuoteStatus.Referred.id) {
          return 'Pending Underwriter';
        }
      },
    },
    {
      field: QuoteColumnField.ReasonForReferral,
      headerName: 'Reason For Referral',
      type: ColumnType.String,
      flex: 1,
      minWidth: 140,
      maxWidth: 208,
      cellClassName: 'reasonForReferral',
      sortable: false,
      isVisible: () => true,
      renderCell: (params) => {
        const { statusId, underwritingTasks } = params.row as any; // FUTURE TODO: underwritingTasks is missing from type
        let currentValue = '-';
        let tooltipText: string | ReactNode | undefined;

        const underwritingLabels = underwritingTasks.map(
          (el: UnderwritingIssue) => el.underwritingRule.underwritingLabel,
        );
        const uniqueUnderwritingLabels: string[] = [...new Set(underwritingLabels)] as string[];

        if (isUWHomePageView) {
          if (uniqueUnderwritingLabels.length === 1) {
            return uniqueUnderwritingLabels[0];
          } else if (uniqueUnderwritingLabels.length > 1) {
            tooltipText = (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'left',
                }}
              >
                {uniqueUnderwritingLabels.map((label: string) => (
                  <Typography
                    key={label}
                    variant='bodySmall'
                    sx={{
                      color: 'common.white',
                      padding: 0,
                    }}
                  >{`• ${label}`}</Typography>
                ))}
              </Box>
            );
            currentValue = 'Hover to view';

            return (
              <Tooltip title={tooltipText}>
                <Typography variant='bodySmall'>{currentValue}</Typography>
              </Tooltip>
            );
          } else if (statusId === QuoteStatus.Referred.id) {
            return 'All reasons have been closed';
          }
        }

        // FUTURE TODO: apparently should dedupe description
        if (underwritingTasks.length > 0) {
          tooltipText = (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              {underwritingTasks.map((el: UnderwritingIssue) => (
                <Typography
                  key={el.id}
                  variant='bodySmall'
                  sx={{
                    color: 'common.white',
                    padding: 0,
                  }}
                >{`• ${el.underwritingRule.description}`}</Typography>
              ))}
            </Box>
          );
          currentValue = underwritingTasks[0].underwritingRule.description;

          return (
            <Tooltip title={tooltipText}>
              <Typography variant='bodySmall'>{currentValue}</Typography>
            </Tooltip>
          );
        } else if (statusId === QuoteStatus.Referred.id) {
          return 'All reasons have been closed';
        }
      },
    },
    {
      field: QuoteColumnField.ModifiedDate,
      headerName: 'Last Modified',
      flex: 0.5,
      minWidth: 110,
      getApplyQuickFilterFn: getApplyStringFilter,
      sortComparator: dateComparator,
      valueGetter: (value, row) =>
        row.updatedAt
          ? format(
              new Date(row.updatedAt),
              getDateFormatter(userConfig.layoutPreferences.clockFormat, LongDateFormatWithoutSeconds),
            )
          : '-',
      isVisible: () => true,
    },
    {
      field: QuoteColumnField.StatusChangeDate,
      headerName: 'Status Change Date',
      flex: 0.5,
      minWidth: 110,
      getApplyQuickFilterFn: getApplyStringFilter,
      sortComparator: dateComparator,
      valueGetter: (value, row) =>
        row?.quoteLatestStatusChange?.latestStatusChangeDate
          ? format(
              new Date(row.quoteLatestStatusChange.latestStatusChangeDate),
              getDateFormatter(userConfig.layoutPreferences.clockFormat, LongDateFormatWithoutSeconds),
            )
          : '-',
      isVisible: () => true,
    },
    {
      field: QuoteColumnField.CreatedAt,
      headerName: 'Created Date',
      flex: 0.5,
      minWidth: 110,
      getApplyQuickFilterFn: getApplyStringFilter,
      sortComparator: dateComparator,
      valueGetter: (value, row) =>
        row.createdAt
          ? format(
              new Date(row.createdAt),
              getDateFormatter(userConfig.layoutPreferences.clockFormat, LongDateFormatWithoutSeconds),
            )
          : '-',
      isVisible: () => true,
    },

    {
      field: QuoteColumnField.CreatedByUser,
      headerName: 'Created By',
      type: ColumnType.String,
      flex: 0.5,
      minWidth: 130,
      sortComparator: disableComparator,
      valueGetter: (value, row) =>
        row.createdByUser?.firstName && row.createdByUser?.lastName
          ? `${row.createdByUser?.firstName} ${row.createdByUser?.lastName}`
          : '-',
      isVisible: () => true,
    },
    {
      field: QuoteColumnField.QuoteSource,
      headerName: 'Quote Source',
      type: ColumnType.String,
      minWidth: 120,
      sortable: true,
      valueGetter: (value, row) =>
        row.reportingQuoteSource ? ReportingQuoteSourceLabels[row.reportingQuoteSource] : '-',
      isVisible: () =>
        isInternalUser &&
        (viewType === QuoteDataGridType.QuoteTypeNewBusiness || UnderwriterHomePageTabs.includes(viewType)),
    },
    {
      field: QuoteColumnField.QuoteOrigin,
      headerName: 'Quote Origin',
      type: ColumnType.String,
      minWidth: 100,
      sortable: false,
      valueGetter: (value, row) => getQuoteOriginLabel(row.quoteSource?.name),
      isVisible: () =>
        isInternalUser &&
        (viewType === QuoteDataGridType.QuoteTypeNewBusiness || UnderwriterHomePageTabs.includes(viewType)),
    },
    {
      field: QuoteColumnField.ComplianceReviewDate,
      headerName: 'Compliance Review Date',
      type: ColumnType.String,
      flex: 0.5,
      minWidth: 160,
      sortable: false,
      isVisible: () =>
        isInternalUser &&
        (viewType === QuoteDataGridType.QuoteTypeRenewal || viewType === QuoteDataGridType.UWHomePageRenewals),
      valueGetter: (value, row) => {
        const complianceReviewDate = row.policyNumber?.policies?.[0]?.complianceReviewDate;
        return complianceReviewDate ? formatDateToTimezone(complianceReviewDate, ShortDateFormat) : '-';
      },
    },
    {
      field: QuoteColumnField.Menu,
      type: ColumnType.Action,
      width: 40,
      isVisible: () => true,
      valueGetter: () => undefined,
      sortable: false,
      mobileConfig: {
        hidden: { xs: true },
        actionMenu: true,
      },
      renderCell: (params) => {
        const menuItemsBase = [
          {
            type: MenuItemType.Link,
            actionName: `/quote/${params.row.id}`,
            text: 'View Details',
            isVisible: true,
          },
          {
            type: MenuItemType.Default,
            text: 'View Duplicates',
            isVisible: params.row.hasDuplicateQuotes,
            handleOnClick: () => {
              modalConfig.showDuplicateQuoteModal(params.row.id);
            },
          },
          {
            type: MenuItemType.Default,
            text: 'Favorite Quote',
            isVisible: !params.row.isPinned,
            handleOnClick: () => {
              handlePinnedQuoteChange({ quoteId: params.row.id, isCurrentlyPinned: false });
            },
          },
          {
            type: MenuItemType.Default,
            text: 'Unfavorite Quote',
            isVisible: params.row.isPinned,
            handleOnClick: () => {
              handlePinnedQuoteChange({ quoteId: params.row.id, isCurrentlyPinned: true });
            },
          },
          {
            type: MenuItemType.Default,
            text: 'Flag as Important',
            isVisible: false, //!params.row.uwFlaggedAsImportant,
            handleOnClick: () => {
              modalConfig.updateQuote({ quoteId: params.row.id, uwFlaggedAsImportant: true });
            },
          },
          {
            type: MenuItemType.Default,
            text: 'Unflag as Important',
            isVisible: false, //params.row.uwFlaggedAsImportant,
            handleOnClick: () => {
              modalConfig.updateQuote({ quoteId: params.row.id, uwFlaggedAsImportant: false });
            },
          },
          {
            type: MenuItemType.Default,
            text: 'Enable Notifications',
            isVisible: !params.row.isWatched && !isUWHomePageView,
            handleOnClick: () => {
              modalConfig.createWatchedQuote({
                quoteId: params.row.id,
                userId: currentUserId,
              });
            },
          },
          {
            type: MenuItemType.Default,
            text: 'Disable Notifications',
            isVisible: params.row.isWatched && !isUWHomePageView,
            handleOnClick: () => {
              modalConfig.deleteWatchedQuote({
                quoteId: params.row.id,
                userId: currentUserId,
              });
            },
          },
          {
            type: MenuItemType.Default,
            text: 'Cancel Quote',
            // all internal except lio sales
            isVisible:
              isInternalWithoutSales &&
              params.row.statusId !== undefined &&
              cancelDeclineQuoteStatuses.includes(params.row.statusId as QuoteStatusId),
            handleOnClick: () => {
              {
                const policyNumber = params.row.policyNumber?.policyNumber ? params.row.policyNumber.policyNumber : '';
                const quoteId = params.row.id;
                modalConfig.showCancelQuoteModal(quoteId, policyNumber);
              }
            },
          },
          {
            type: MenuItemType.Default,
            text: 'Flag for Referral',
            isVisible:
              (isUnderwriter || isLIOContractor) &&
              params.row.statusId !== undefined &&
              quoteStatuses.includes(params.row.statusId as QuoteStatusId),
            handleOnClick: () => {
              modalConfig.showFlagForReferralModal(params.row.id);
            },
          },
          {
            type: MenuItemType.Default,
            text: 'Decline Quote',
            isVisible:
              isInternalWithoutSales &&
              params.row.statusId !== undefined &&
              cancelDeclineQuoteStatuses.includes(params.row.statusId as QuoteStatusId),
            handleOnClick: () => {
              const row = params.row;
              const policyNumber = row.policyNumber?.policyNumber ? row.policyNumber.policyNumber : '';
              const quoteId = row.id;
              const quoteStatus = row.statusId;
              const quoteCustomerId = row.customer.id;
              const quoteBrokerageId = row.brokerage.id;
              const hasInternalOnlyAccessRestriction = row.hasInternalOnlyAccessRestriction ?? false;
              modalConfig.showDeclineQuoteModal(
                quoteId,
                policyNumber,
                quoteStatus,
                quoteCustomerId,
                quoteBrokerageId,
                hasInternalOnlyAccessRestriction,
              );
            },
          },
          {
            type: MenuItemType.Default,
            text: 'Copy Quote',
            isVisible: () => {
              if (isUWHomePageView) {
                return false;
              }
              if (params.row.quoteType === QuoteType.Renewal) return false;
              const { isUnderwriter, isCSR, isSrOps, isJrOps, isPortalAdmin, isAdmin, isBroker } = userAuth;

              if (isBroker || isCSR) {
                return [
                  QuoteStatusId.Declined,
                  QuoteStatusId.Canceled,
                  QuoteStatusId.Rejected,
                  QuoteStatusId.Expired,
                ].includes(params.row.statusId as QuoteStatusId);
              }
              return isUnderwriter || isLIOContractor || isSrOps || isJrOps || isPortalAdmin || isAdmin || isBroker;
            },
            handleOnClick: () => {
              modalConfig.showCopyQuoteModal(params.row.id);
            },
          },
          {
            type: MenuItemType.Default,
            text: 'Manage Roles',
            isVisible: isInternalUser,
            handleOnClick: () => {
              modalConfig.showRoleManagementModal(params.row.id);
            },
          },
        ];

        return (
          <MenuButton
            icon={<EllipsisH />}
            items={menuItemsBase.filter((item) =>
              typeof item.isVisible === 'function' ? item.isVisible() : item?.isVisible,
            )}
          />
        );
      },
    },
  ];

  return baseColumns.filter((column) => column.isVisible?.(viewType));
}
